import React, {
  useState,
  useEffect,
  useCallback,
  useContext,
  useRef,
} from "react";
import { Navbar, Nav, Container } from "react-bootstrap";
import { Link, useLocation, useNavigate } from "react-router-dom";
import "./../../../assets/css/general/NavigationBar/NavigationBar.css";
import logo from "./../../../assets/images/logo-removebg-preview.png";

// ── Dropdown configs ─────────────────────────────────────────────────────────
const STUDENTS_ITEMS = [
  {
    icon: "🧠",
    title: "AI Tutor",
    desc: "Personalized guide that teaches, explains and solves doubts interactively.",
    path: "/ai-tutor",
  },
   {
    icon: "🤝",
    title: "AI Buddy",
    desc: "Friendly revision and practice partner — collaborate and build routine.",
    path: "/ai-buddy",
  },
];

const RESOURCES_ITEMS = [
  { icon: "📝", title: "Blog", path: "/blog" },
  { icon: "🛠️", title: "Help & Support", path: "/help" }, // ✅ linked
  { icon: "❓", title: "FAQs", path: "/faqs" },
  { icon: "📚", title: "Courses", path: "/courses" },
  { icon: "🗺️", title: "Roadmaps", path: "/build-roadmap" },
];

const ABOUT_ITEMS = [
  { icon: "🏢", title: "About Us", path: "/aboutus" },
  { icon: "💼", title: "Careers", path: "/careers" },
  { icon: "📩", title: "Contact Us", path: "/contact" },
];

const NAV_PLAIN = [{ path: "/", label: "Home" }];

// ── Reusable Dropdown ────────────────────────────────────────────────────────
const NavDropdown = ({ label, children, isSolid }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  return (
    <div
      className={`tmh-dropdown${open ? " tmh-dropdown--open" : ""}`}
      ref={ref}
    >
      <button
        className={`tmh-nav__link tmh-nav__link--dd${open ? " tmh-nav__link--active" : ""}`}
        onClick={() => setOpen((p) => !p)}
        aria-expanded={open}
        type="button"
      >
        {label}
        <span className={`tmh-dd-arrow${open ? " tmh-dd-arrow--open" : ""}`}>
          ▾
        </span>
      </button>

      {open && (
        <div className="tmh-dd-panel" onClick={() => setOpen(false)}>
          {children}
        </div>
      )}
    </div>
  );
};

// ── NavigationBar ────────────────────────────────────────────────────────────
const NavigationBar = () => {
  // const { role } = useContext(userContext);
  const [expanded, setExpanded] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  // ── NEW: unread notifications count (replace with real data later) ──
  const [notifCount, setNotifCount] = useState(3);

  const location = useLocation();
  const navigate = useNavigate();
  const navRef = useRef(null);

  const isHomePage = location.pathname === "/";
  const isSolid = true;

  // highlight active icon links
  const isHelpActive = location.pathname === "/help";
  const isNotifActive = location.pathname === "/notifications";

  // ── Scroll detection — works on ALL pages ──
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(
        window.pageYOffset > 60 ||
          window.scrollY > 60 ||
          document.documentElement.scrollTop > 60,
      );
    };
    handleScroll(); // run once on mount
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // ── Kill Bootstrap padding ──
  useEffect(() => {
    const kill = () => {
      document.body.style.paddingTop = "0px";
      document.body.style.marginTop = "0px";
    };
    kill();
    const obs = new MutationObserver(kill);
    obs.observe(document.body, {
      attributes: true,
      attributeFilter: ["style"],
    });
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    setExpanded(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!expanded) return;
    const handler = (e) => {
      if (navRef.current && !navRef.current.contains(e.target))
        setExpanded(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [expanded]);

  useEffect(() => {
    document.body.style.overflow = expanded ? "hidden" : "auto";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [expanded]);

  const handleNavigate = useCallback(
    (path) => {
      navigate(path);
      setExpanded(false);
    },
    [navigate],
  );

  const handleToggle = useCallback(() => setExpanded((p) => !p), []);

  // clear badge when user opens notifications page
  const handleNotifClick = useCallback(() => {
    setNotifCount(0);
    navigate("/notifications");
    setExpanded(false);
  }, [navigate]);

  const navbarClass = `tmh-navbar${isSolid ? " tmh-navbar--scrolled" : " tmh-navbar--top"}${scrolled ? " tmh-navbar--shrunk" : ""}`;

  return (
    <Navbar
      ref={navRef}
      expanded={expanded}
      onToggle={setExpanded}
      expand="lg"
      className={navbarClass}
      as="header"
    >
      <Container fluid className="tmh-container">
        {/* ── Logo ── */}
        <Navbar.Brand
          as={Link}
          to="/"
          className="tmh-logo"
          aria-label="Tech Mentor Hub"
        >
          <img
            src={logo}
            alt="Tech Mentor Hub"
            className={`tmh-logo__img${isSolid ? " tmh-logo__img--dark" : " tmh-logo__img--light"}`}
          />
        </Navbar.Brand>

        {/* ── Mobile Toggler ── */}
        <button
          className={`tmh-toggler${isSolid ? " tmh-toggler--scrolled" : " tmh-toggler--top"}${expanded ? " tmh-toggler--open" : ""}`}
          type="button"
          onClick={handleToggle}
          aria-label={expanded ? "Close menu" : "Open menu"}
          aria-expanded={expanded}
          aria-controls="tmh-nav-collapse"
        >
          <span className="tmh-toggler__bar" />
          <span className="tmh-toggler__bar" />
          <span className="tmh-toggler__bar" />
        </button>

        {/* ── Collapse ── */}
        <Navbar.Collapse id="tmh-nav-collapse" className="tmh-collapse">
          <Nav className="tmh-nav" as="nav" aria-label="Primary">
            {/* Home */}
            {NAV_PLAIN.map(({ path, label }) => {
              const isActive = location.pathname === path;
              return (
                <Nav.Link
                  key={path}
                  as={Link}
                  to={path}
                  onClick={() => setExpanded(false)}
                  className={`tmh-nav__link${isActive ? " tmh-nav__link--active" : ""}`}
                  aria-current={isActive ? "page" : undefined}
                >
                  {label}
                </Nav.Link>
              );
            })}

            {/* For Students */}
            <NavDropdown label="For Students" isSolid={isSolid}>
              <div className="tmh-dd-mega">
                <p className="tmh-dd-heading">AI Companions</p>
                <div className="tmh-dd-cards">
                  {STUDENTS_ITEMS.map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      className="tmh-dd-card"
                    >
                      <span className="tmh-dd-card-icon">{item.icon}</span>
                      <div>
                        <p className="tmh-dd-card-title">{item.title}</p>
                        <p className="tmh-dd-card-desc">{item.desc}</p>
                        <span className="tmh-dd-card-link">Learn More</span>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </NavDropdown>

            {/* Resources — Help & Support is inside here ✅ */}
            <NavDropdown label="Resources" isSolid={isSolid}>
              <div className="tmh-dd-list">
                {RESOURCES_ITEMS.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`tmh-dd-list-item${location.pathname === item.path ? " tmh-dd-list-item--active" : ""}`}
                  >
                    <span className="tmh-dd-list-icon">{item.icon}</span>
                    {item.title}
                  </Link>
                ))}
              </div>
            </NavDropdown>

            {/* About */}
            <NavDropdown label="About" isSolid={isSolid}>
              <div className="tmh-dd-list">
                {ABOUT_ITEMS.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className="tmh-dd-list-item"
                  >
                    <span className="tmh-dd-list-icon">{item.icon}</span>
                    {item.title}
                  </Link>
                ))}
              </div>
            </NavDropdown>
          </Nav>

          {/* ── Actions ── */}
          <div className="tmh-actions">
            {/* ── NOTIFICATIONS ICON BUTTON ✅ ── */}
            <button
              className={`tmh-icon-btn${isNotifActive ? " tmh-icon-btn--active" : ""}`}
              onClick={handleNotifClick}
              type="button"
              aria-label="Notifications"
              title="Notifications"
            >
              {/* Bell SVG */}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                <path d="M13.73 21a2 2 0 0 1-3.46 0" />
              </svg>
              {/* Red badge — shows when there are unread notifications */}
              {notifCount > 0 && (
                <span className="tmh-icon-btn__badge">{notifCount}</span>
              )}
            </button>

            {/* ── HELP ICON BUTTON ✅ ── */}
            <button
              className={`tmh-icon-btn${isHelpActive ? " tmh-icon-btn--active" : ""}`}
              onClick={() => handleNavigate("/help")}
              type="button"
              aria-label="Help and Support"
              title="Help & Support"
            >
              {/* Question mark SVG */}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="10" />
                <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
            </button>

            <Link
              to="/mentor-auth"
              className={`tmh-actions__mentor${isSolid ? " tmh-actions__mentor--scrolled" : " tmh-actions__mentor--top"}`}
              onClick={() => setExpanded(false)}
            >
              Become a Mentor
            </Link>

            <button
              className={`tmh-btn tmh-btn--secondary${isSolid ? " tmh-btn--secondary-light" : " tmh-btn--secondary-dark"}`}
              onClick={() => handleNavigate("/login")}
              type="button"
            >
              Login
            </button>

            <button
              className={`tmh-btn tmh-btn--primary${isSolid ? " tmh-btn--primary-light" : " tmh-btn--primary-dark"}`}
              onClick={() => handleNavigate("/signup")}
              type="button"
            >
              Sign Up
            </button>
          </div>
        </Navbar.Collapse>
      </Container>
    </Navbar>
  );
};

export default NavigationBar;
